import {
  BenchmarkRows,
  MetricRow,
} from "@/components/terminal/detail/metric-row"
import type {
  TerminalFileSectionModel,
  TerminalLicenseRowModel,
} from "@/lib/terminal/company-file"
import {
  metricDefinitions,
  sectionDefinitions,
} from "@/lib/terminal/file-label-definitions"
import { cn } from "@/lib/utils"

import { DefinitionLabel } from "./definition-tooltip"
import { PlatformList } from "./platform-list"

export const FileDetailSection = ({
  section,
}: {
  section: TerminalFileSectionModel
}) => {
  switch (section.kind) {
    case "metrics":
      return (
        <FileBox label={section.title}>
          {section.rows.map((row, index) => (
            <div key={row.key}>
              <MetricRow row={row} isFirst={index === 0} />
              <BenchmarkRows rows={row.benchmarks} />
            </div>
          ))}
          {section.platforms && (
            <div
              className={cn(
                section.rows.length > 0 && "file-row-rule mt-1 pt-1"
              )}
            >
              <PlatformList platforms={section.platforms} />
            </div>
          )}
        </FileBox>
      )
    case "trade":
      return (
        <FileBox label={section.title}>
          {section.rows.map((row, index) => (
            <div
              key={row.key}
              className={cn(
                "file-row items-center",
                index > 0 && "file-row-rule"
              )}
            >
              <DefinitionLabel
                definition={metricDefinitions[row.label]}
                className="type-row-label text-muted-foreground min-w-0 flex-1"
              >
                {row.label}
              </DefinitionLabel>
              <span className="flex w-[min(var(--file-crosstrade-cell-width),58vw)] shrink-0 items-center justify-end gap-2">
                {row.share === "--" ? (
                  <span className="type-data text-muted-foreground w-[var(--file-crosstrade-figures-width)] text-right">
                    —
                  </span>
                ) : (
                  <span className="type-data text-foreground inline-grid min-w-[var(--file-crosstrade-figures-width)] grid-cols-[minmax(var(--file-crosstrade-share-width),max-content)_8px_minmax(var(--file-crosstrade-sat-width),max-content)] items-baseline justify-end gap-x-1 text-right whitespace-nowrap">
                    <TradeDatum value={row.share} label="share" />
                    <span className="type-micro text-muted-foreground text-center font-normal tracking-normal normal-case">
                      ·
                    </span>
                    <TradeDatum value={row.satisfaction} label="sat" />
                  </span>
                )}
              </span>
            </div>
          ))}
          {section.servicesMarketed.length > 0 && (
            <div
              className={cn(
                "file-row items-center gap-3",
                section.rows.length > 0 && "file-row-rule"
              )}
            >
              <DefinitionLabel
                definition={metricDefinitions["Services Marketed"]}
                className="type-row-label text-muted-foreground shrink-0 whitespace-nowrap"
              >
                Services Marketed
              </DefinitionLabel>
              <span className="type-data text-foreground min-w-0 flex-1 truncate text-right">
                {section.servicesMarketed.join(", ")}
              </span>
            </div>
          )}
        </FileBox>
      )
    case "kv":
      return (
        <FileBox label={section.title}>
          {section.rows.map((row, index) => (
            <div
              key={row.key}
              className={cn(
                "file-row items-center",
                index > 0 && "file-row-rule"
              )}
            >
              <span className="type-row-label text-foreground w-[var(--file-kv-label-width)] shrink-0">
                {row.label}
              </span>
              {row.href ? (
                <a
                  href={row.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="type-row-label text-terminal-accent min-w-0 flex-1 truncate no-underline hover:underline"
                >
                  {row.value}
                </a>
              ) : (
                <span className="type-row-label text-muted-foreground min-w-0 flex-1">
                  {row.value}
                </span>
              )}
            </div>
          ))}
          {section.licensedIn.length > 0 && (
            <div
              className={cn(
                "file-row items-start",
                section.rows.length > 0 && "file-row-rule"
              )}
            >
              <span className="type-row-label text-foreground w-[var(--file-kv-label-width)] shrink-0">
                Licensed In
              </span>
              <span className="type-row-label text-muted-foreground min-w-0 flex-1">
                {section.licensedIn.join(", ")}
              </span>
            </div>
          )}
          {section.licenseRows.map((row, index) => (
            <LicenseRow
              key={row.key}
              row={row}
              hasRule={
                section.rows.length > 0 ||
                section.licensedIn.length > 0 ||
                index > 0
              }
            />
          ))}
        </FileBox>
      )
    case "timeline":
      return (
        <FileBox label={section.title}>
          <div className="border-border mt-1 border-t pt-2.5">
            <div className="relative pl-4">
              <div className="bg-border-strong absolute top-1.5 bottom-1.5 left-0.75 w-px" />
              {section.rows.map((row, index) => (
                <div
                  key={row.key}
                  className={cn(
                    "relative",
                    index < section.rows.length - 1 && "pb-3"
                  )}
                >
                  <div className="bg-text-secondary border-border-strong absolute top-1.25 -left-4 size-1.25 rounded-full border" />
                  <div className="type-micro text-muted-foreground mb-0.5 font-normal tracking-normal tabular-nums">
                    {row.date} · {row.sourceLabel}
                  </div>
                  <div className="type-micro mb-0.5">{row.label}</div>
                  <div className="text-muted-foreground leading-normal font-normal">
                    {row.notes}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FileBox>
      )
  }
}

// A titled File box: the label sits on the border.
export const FileBox = ({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) => (
  <section
    className={cn(
      "border-border-strong relative mt-4 mb-6 rounded-sm border px-4 pt-4 pb-2.5 [--row-rule-inset:16px]",
      className
    )}
  >
    <DefinitionLabel
      definition={sectionDefinitions[label]}
      className="type-heading float-label bg-background"
    >
      {label}
    </DefinitionLabel>
    {children}
  </section>
)

const LicenseRow = ({
  row,
  hasRule,
}: {
  row: TerminalLicenseRowModel
  hasRule: boolean
}) => (
  <div
    className={cn(
      "grid min-h-(--file-row-min-height) grid-cols-[var(--file-license-state-width)_minmax(0,1fr)_auto] items-start gap-3 py-(--file-row-padding-y) pl-[var(--file-kv-label-width)]",
      hasRule && "file-row-rule"
    )}
  >
    <span className="type-row-label text-muted-foreground truncate">
      {row.state}
    </span>
    <span className="type-row-label text-muted-foreground min-w-0 truncate">
      {row.licenseType}
    </span>
    <span className="type-micro text-muted-foreground font-normal tracking-normal tabular-nums">
      {row.licenseNumber}
    </span>
  </div>
)

const TradeDatum = ({ value, label }: { value: string; label: string }) => {
  if (value === "--") {
    return <span className="text-muted-foreground text-right">—</span>
  }

  return (
    <span className="inline-flex items-baseline justify-end gap-1">
      <span>{value.replace(` ${label}`, "")}</span>
      <span className="type-micro text-muted-foreground font-normal tracking-normal normal-case">
        {label}
      </span>
    </span>
  )
}
